import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import {
  mapAdminUser,
  type AdminUsersPageViewModel,
  type AdminUserViewModel,
} from '@/features/admin-users/api';
import {
  mapFederatedIdentity,
  type FederatedIdentitiesQuery,
  type FederatedIdentityViewModel,
} from '@/features/admin-federated-identities/api';
import type { UserDto } from '@/features/admin-users/endpoints';
import type { UserPickerOption } from '@/components/ui/user-picker';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

import type {
  ApplyTenantTemplateRequestDto,
  BaseVersionsDto,
  ClusterGrantCapability,
  ClusterGrantDto,
  ClusterGrantFilters,
  CreateClusterGrantRequestDto,
  CreateTenantRequestDto,
  CreateTenantTemplateRequestDto,
  DeploymentManagerHealthDto,
  DeploymentStatus,
  DockerHubCredentialsDto,
  JobExecutionDto,
  JobLaunchDto,
  JobStatus,
  ListJobsParams,
  OverridableServiceName,
  OverrideJobLaunchDto,
  PermissionVocabularyDto,
  ProvisionTenantUserRequestDto,
  PutDockerHubCredentialsRequestDto,
  ReplaceTenantUserRolesRequestDto,
  ServiceOverrideRequestDto,
  TenantDto,
  TenantRoleDto,
  TenantState,
  TenantTemplateDto,
  UpdateTenantRequestDto,
} from './endpoints';
import { ApiError } from '@/api/errors';
import { shouldRetry } from '@/query/client';
import { isNonTerminalDeploymentStatus } from './overrides';

/**
 * View-model layer + React Query hooks for the Platform console (PTV2-1021).
 * Mirrors the admin-configs pattern: raw snake_case wire DTOs from
 * `endpoints.ts` are mapped to camelCase view models here, and all server
 * state flows through TanStack Query (no Zustand). Query keys are kept local
 * to this feature (rooted at `platformRootKey`) so a single invalidation
 * refreshes every tenant/grant/template slice — same self-contained approach
 * admin-configs uses.
 */

const POLL_INTERVAL_MS = 2_000;

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

export type { TenantState, JobStatus, ClusterGrantCapability, DeploymentStatus, OverridableServiceName };

export interface ServiceOverrideViewModel {
  readonly serviceName: OverridableServiceName;
  readonly imageTag: string;
  readonly imageDigestAtDeploy: string | null;
}

export interface TenantViewModel {
  readonly uuid: string;
  readonly name: string;
  readonly label: string;
  readonly schemaName: string;
  readonly state: TenantState;
  readonly schemaInitialized: boolean;
  readonly createdOn: string | null;
  // ── Phase 2 override fields ──
  readonly serviceOverrides: readonly ServiceOverrideViewModel[];
  readonly hasOverrides: boolean;
  readonly deploymentStatus: DeploymentStatus | null;
  readonly currentJobExecutionId: number | null;
}

/** DM-health view model. `available:false` is NOT an error — it means prod/staging. */
export interface DmHealthViewModel {
  readonly available: boolean;
  readonly resourceUsage: {
    readonly overrideMemoryBytes: number;
    readonly overrideMemoryCeilingBytes: number;
    readonly overrideTenantCount: number;
  } | null;
}

export interface DockerHubCredentialsViewModel {
  readonly username: string;
  readonly tokenSet: boolean;
  readonly updatedOn: string | null;
}

/**
 * Base image tags the cluster currently runs, used to pre-fill the create-tenant
 * override inputs (PTV2-1021 Phase 4). A `null` value means that service is not
 * deployed (e.g. indexing in dev) → leave its input empty. The wire shape is
 * already camelCase (see `BaseVersionsDto`); this is a pass-through, no mapping.
 * When the DM is unavailable the hook resolves an all-null map so the dialog
 * simply shows empty inputs (no error, current behavior).
 */
export interface BaseVersionsViewModel {
  readonly backend: string | null;
  readonly frontend: string | null;
  readonly indexing: string | null;
}

export interface JobExecutionViewModel {
  /** `jobId` is a string on the wire; kept as-is. */
  readonly jobId: string;
  readonly jobName: string;
  readonly status: JobStatus;
  readonly startTime: string | null;
  readonly endTime: string | null;
  /** From the nested `exitStatus.exitCode` (e.g. `EXECUTING`, `FAILED`). */
  readonly exitCode: string;
  /** From the nested `exitStatus.exitDescription` — human-readable failure reason (full stack trace). */
  readonly exitDescription: string;
  /**
   * Who launched the job, from the `initiatedBy` job parameter (PTV2-1433). `null` when the job
   * carries no such parameter — i.e. a system/automatic launch; the UI renders that as "System".
   */
  readonly initiatedBy: string | null;
  /** Convenience flag: a job in a terminal Batch state (no more polling needed). */
  readonly isTerminal: boolean;
}

export interface ClusterGrantViewModel {
  readonly id: number;
  readonly globalUserGuid: string;
  readonly tenantUuid: string;
  readonly tenantName: string | null;
  readonly capability: ClusterGrantCapability;
  readonly grantedBy: string | null;
  readonly grantedAt: string | null;
  readonly revokedAt: string | null;
  readonly isRevoked: boolean;
}

/**
 * A user inside a tenant, as seen from the tenant-admin reach surface (PTV2-1426). Re-exports the
 * `admin-users` view model verbatim — same `UserDTO` wire shape, just fetched from the tenant-scoped
 * endpoint instead of the global directory.
 */
export type TenantUserViewModel = AdminUserViewModel;
export type TenantUsersPageViewModel = AdminUsersPageViewModel;

/** A role in a tenant's own role catalogue (tenant-admin reach). `id` is the role's guid. */
export interface TenantRoleViewModel {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  /** Built-in (seeded) roles are non-deletable (PTV2-1442). */
  readonly builtIn: boolean;
}

/** One allowed value of a permission-grammar slot (PTV2-1441). */
export interface PermissionTermViewModel {
  readonly value: string;
  readonly label: string;
  readonly group: string | null;
}

export interface PermissionSlotViewModel {
  readonly name: string;
  readonly label: string;
  readonly values: readonly PermissionTermViewModel[];
}

export interface PermissionShapeViewModel {
  readonly id: string;
  readonly label: string;
  readonly template: string;
  readonly editableSlots: readonly string[];
}

/** The strongly-typed permission grammar vocabulary (PTV2-1441). */
export interface PermissionVocabularyViewModel {
  readonly slots: readonly PermissionSlotViewModel[];
  readonly shapes: readonly PermissionShapeViewModel[];
}

export interface TenantTemplateViewModel {
  readonly name: string;
  readonly description: string | null;
  readonly isDefault: boolean;
  readonly createdBy: string | null;
  readonly createdAt: string | null;
  readonly definition: unknown;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

export function mapTenant(dto: TenantDto): TenantViewModel {
  // PTV2-1433: the backend TenantDTO is camelCase and its enum fields carry the UPPERCASE
  // enum names (ACTIVE / READY / BACKEND / …). The FE unions + i18n keys + comparisons are all
  // lowercase, so normalize enum values here. (Write paths send lowercase; the backend
  // TenantState.fromStored upper-cases on the way in, so round-trips are safe.)
  const lower = (v: string | null | undefined): string | null =>
    v == null ? null : v.toLowerCase();
  const serviceOverrides: ServiceOverrideViewModel[] = (dto.serviceOverrides ?? []).map((o) => ({
    serviceName: o.serviceName.toLowerCase() as OverridableServiceName,
    imageTag: o.imageTag,
    imageDigestAtDeploy: o.imageDigestAtDeploy ?? null,
  }));
  return {
    uuid: dto.uuid,
    name: dto.name,
    label: dto.label ?? '',
    schemaName: dto.schemaName,
    state: (lower(dto.state) ?? 'inactive') as TenantState,
    schemaInitialized: dto.schemaInitialized,
    createdOn: dto.createdOn ?? null,
    serviceOverrides,
    hasOverrides: serviceOverrides.length > 0,
    deploymentStatus: lower(dto.deploymentStatus) as DeploymentStatus | null,
    currentJobExecutionId: dto.currentJobExecutionId ?? null,
  };
}

export function mapDmHealth(dto: DeploymentManagerHealthDto): DmHealthViewModel {
  const ru = dto.resourceUsage;
  return {
    available: true,
    resourceUsage:
      ru != null
        ? {
            overrideMemoryBytes: ru.overrideMemoryBytes,
            overrideMemoryCeilingBytes: ru.overrideMemoryCeilingBytes,
            overrideTenantCount: ru.overrideTenantCount,
          }
        : null,
  };
}

/**
 * Pass-through map for base versions — the wire shape is already camelCase
 * (unlike the snake_case tenant DTOs), so we only normalise absent keys to
 * `null`. Defensive: tolerates a partial body (a missing key → null).
 */
export function mapBaseVersions(dto: BaseVersionsDto): BaseVersionsViewModel {
  return {
    backend: dto.backend ?? null,
    frontend: dto.frontend ?? null,
    indexing: dto.indexing ?? null,
  };
}

export function mapDockerHubCredentials(dto: DockerHubCredentialsDto): DockerHubCredentialsViewModel {
  return {
    username: dto.username,
    tokenSet: dto.tokenSet,
    updatedOn: dto.updatedOn ?? null,
  };
}

/** Spring Batch terminal states — polling stops once the job reaches one. */
const TERMINAL_JOB_STATUSES: ReadonlySet<JobStatus> = new Set<JobStatus>([
  'COMPLETED',
  'FAILED',
  'STOPPED',
  'ABANDONED',
]);

export function isTerminalStatus(status: JobStatus): boolean {
  return TERMINAL_JOB_STATUSES.has(status);
}

export function mapJob(dto: JobExecutionDto): JobExecutionViewModel {
  return {
    jobId: dto.jobId,
    jobName: dto.jobName,
    status: dto.status,
    startTime: dto.startTime ?? null,
    endTime: dto.endTime ?? null,
    exitCode: dto.exitStatus?.exitCode ?? '',
    exitDescription: dto.exitStatus?.exitDescription ?? '',
    initiatedBy: extractInitiatedBy(dto.jobParameters),
    isTerminal: isTerminalStatus(dto.status),
  };
}

/**
 * Pull the `initiatedBy` job parameter out of the serialized Spring Batch JobParameters, whose wire
 * shape is `{ parameters: [{ name, value, type, identifying }], ... }`. Returns null when absent
 * (system/automatic launch).
 */
function extractInitiatedBy(jobParameters: Readonly<Record<string, unknown>> | null | undefined): string | null {
  const params = (jobParameters as { parameters?: unknown } | null | undefined)?.parameters;
  if (!Array.isArray(params)) return null;
  for (const p of params) {
    if (p !== null && typeof p === 'object' && (p as { name?: unknown }).name === 'initiatedBy') {
      const value = (p as { value?: unknown }).value;
      return typeof value === 'string' && value.length > 0 ? value : null;
    }
  }
  return null;
}

export function mapClusterGrant(dto: ClusterGrantDto): ClusterGrantViewModel {
  return {
    id: dto.id,
    globalUserGuid: dto.global_user_guid,
    tenantUuid: dto.tenant_uuid,
    tenantName: dto.tenant_name ?? null,
    capability: dto.capability,
    grantedBy: dto.granted_by ?? null,
    grantedAt: dto.granted_at ?? null,
    revokedAt: dto.revoked_at ?? null,
    isRevoked: dto.revoked_at !== null && dto.revoked_at !== undefined,
  };
}

/**
 * True when `error` is the tenant-admin reach's anti-enumeration 404 (PTV2-1426):
 * `TenantAdminGrantFilter` returns an identical 404 whether the tenant is unknown or the caller
 * simply lacks (or lost) a `cluster_user_tenant_grant` covering it, so the UI must never distinguish
 * the two — it just renders "you don't have management access to this tenant".
 */
export function isTenantAdminAccessDenied(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function mapTenantRole(dto: TenantRoleDto): TenantRoleViewModel {
  return {
    id: dto.id ?? '',
    name: dto.name,
    description: dto.description ?? '',
    builtIn: dto.builtIn ?? false,
  };
}

export function mapPermissionVocabulary(dto: PermissionVocabularyDto): PermissionVocabularyViewModel {
  return {
    slots: dto.slots.map((slot) => ({
      name: slot.name,
      label: slot.label,
      values: slot.values.map((term) => ({
        value: term.value,
        label: term.label,
        group: term.group ?? null,
      })),
    })),
    shapes: dto.shapes.map((shape) => ({
      id: shape.id,
      label: shape.label,
      template: shape.template,
      editableSlots: [...shape.editableSlots],
    })),
  };
}

export function mapTenantTemplate(dto: TenantTemplateDto): TenantTemplateViewModel {
  return {
    name: dto.name,
    description: dto.description ?? null,
    isDefault: dto.is_default ?? false,
    createdBy: dto.created_by ?? null,
    createdAt: dto.created_at ?? null,
    definition: dto.definition ?? null,
  };
}

// ---------------------------------------------------------------------------
// Query keys (local to the platform feature; see dev-decisions D1)
// ---------------------------------------------------------------------------

export const platformRootKey = ['platform'] as const;
export const tenantsRootKey = [...platformRootKey, 'tenants'] as const;
export const clusterGrantsRootKey = [...platformRootKey, 'cluster-grants'] as const;
export const tenantTemplatesKey = [...platformRootKey, 'tenant-templates'] as const;
export const dmHealthKey = [...platformRootKey, 'dm-health'] as const;
export const baseVersionsKey = [...platformRootKey, 'base-versions'] as const;
export const dockerHubCredentialsKey = [...platformRootKey, 'dockerhub-credentials'] as const;

export function tenantsListQueryKey(
  page: number,
  size: number,
): readonly [...typeof tenantsRootKey, 'list', number, number] {
  return [...tenantsRootKey, 'list', page, size] as const;
}

export function tenantQueryKey(
  uuid: string,
): readonly [...typeof tenantsRootKey, 'detail', string] {
  return [...tenantsRootKey, 'detail', uuid] as const;
}

export function jobQueryKey(
  jobExecutionId: number,
): readonly [...typeof platformRootKey, 'jobs', number] {
  return [...platformRootKey, 'jobs', jobExecutionId] as const;
}

export const jobsListKey = [...platformRootKey, 'jobs', 'list'] as const;

/** `[...tenant, 'users', 'list', page, size, q]` — `q` (trimmed) is baked in so a search refetches. */
export function tenantUsersListQueryKey(
  tenantUuid: string,
  page: number,
  size: number,
  q = '',
): readonly [...typeof tenantsRootKey, string, 'users', 'list', number, number, string] {
  return [...tenantsRootKey, tenantUuid, 'users', 'list', page, size, q.trim()] as const;
}

export function tenantRolesListQueryKey(
  tenantUuid: string,
  q = '',
): readonly [...typeof tenantsRootKey, string, 'roles', 'list', string] {
  return [...tenantsRootKey, tenantUuid, 'roles', 'list', q.trim()] as const;
}

/** A single role in a tenant's catalogue (PTV2-1444 role page). */
export function tenantRoleQueryKey(
  tenantUuid: string,
  guid: string,
): readonly [...typeof tenantsRootKey, string, 'roles', 'detail', string] {
  return [...tenantsRootKey, tenantUuid, 'roles', 'detail', guid] as const;
}

/** A single role's grammar permissions (PTV2-1442). */
export function tenantRolePermissionsQueryKey(
  tenantUuid: string,
  guid: string,
): readonly [...typeof tenantsRootKey, string, 'roles', string, 'permissions'] {
  return [...tenantsRootKey, tenantUuid, 'roles', guid, 'permissions'] as const;
}

/** The permission vocabulary is static per build; its own key, cached indefinitely (PTV2-1441). */
export const permissionVocabularyKey = [...platformRootKey, 'permission-vocabulary'] as const;

export function clusterGrantsListQueryKey(
  filters: ClusterGrantFilters,
): readonly [...typeof clusterGrantsRootKey, 'list', string, string] {
  return [...clusterGrantsRootKey, 'list', filters.globalUserGuid ?? '', filters.tenantUuid ?? ''] as const;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface TenantListResult {
  readonly tenants: readonly TenantViewModel[];
  readonly totalElements: number;
  readonly totalPages: number;
}

/**
 * Tenant list. Per the contract this polls every 2 s while any tenant carries a
 * non-terminal `deploymentStatus`. Phase 1 has no `deploymentStatus` (overrides
 * are Phase 2), so the interval function always returns `false` — the wiring
 * point for Phase 2 is the `hasNonTerminalTenant` helper below.
 */
export function useTenants(page = 0, size = 50): UseQueryResult<TenantListResult> {
  return useQuery<TenantListResult>({
    queryKey: tenantsListQueryKey(page, size),
    queryFn: async ({ signal }) => {
      const dto = await apiClient.listTenants(page, size, { signal });
      return {
        tenants: dto.content.map(mapTenant),
        totalElements: dto.totalElements,
        totalPages: dto.totalPages,
      };
    },
    refetchInterval: (query) =>
      hasNonTerminalTenant(query.state.data?.tenants ?? []) ? POLL_INTERVAL_MS : false,
  });
}

/**
 * Returns `true` once any tenant carries a non-terminal deployment status
 * (`pending | pulling | deploying | wiping`), which is what drives `useTenants`
 * to poll every 2 s. Plain tenants (null status) and terminal states do not
 * poll. (Phase 2: flipped from the Phase 1 always-false stub.)
 */
export function hasNonTerminalTenant(tenants: readonly TenantViewModel[]): boolean {
  return tenants.some((t) => isNonTerminalDeploymentStatus(t.deploymentStatus));
}

const DM_HEALTH_REFETCH_MS = 60_000;

/**
 * Deployment-manager reachability probe. Gates EVERY override affordance.
 *
 * Per the contract a 404/503/network error means "feature unavailable"
 * (staging/prod) and must NOT surface as an error state — it maps to
 * `{ available: false }`. Only a 200 maps to `{ available: true }`. A 401 still
 * flows through the http layer's global redirect (re-login), so we don't
 * special-case it. `retry:false` (a probe), `refetchInterval` 60 s while the
 * Platform section is mounted.
 */
export function useDmHealth(): UseQueryResult<DmHealthViewModel> {
  return useQuery<DmHealthViewModel>({
    queryKey: dmHealthKey,
    retry: false,
    refetchInterval: DM_HEALTH_REFETCH_MS,
    queryFn: async ({ signal }) => {
      try {
        return mapDmHealth(await apiClient.getDeploymentManagerHealth({ signal }));
      } catch (error) {
        // 404 (not in discovery) / 503 (down) / network → feature unavailable.
        // Anything else that is a transport/HTTP failure is also treated as
        // unavailable so a flaky probe never blocks the rest of the Tenants tab.
        if (error instanceof ApiError) {
          return { available: false, resourceUsage: null };
        }
        throw error;
      }
    },
  });
}

const UNAVAILABLE_DM_HEALTH: DmHealthViewModel = { available: false, resourceUsage: null };

/** Convenience: the DM-health view model with an unavailable default while loading. */
export function useDmAvailable(): { available: boolean; health: DmHealthViewModel } {
  const query = useDmHealth();
  const health = query.data ?? UNAVAILABLE_DM_HEALTH;
  return { available: health.available, health };
}

const BASE_VERSIONS_STALE_MS = 5 * 60_000;
const NO_BASE_VERSIONS: BaseVersionsViewModel = {
  backend: null,
  frontend: null,
  indexing: null,
};

/**
 * Base image tags the cluster currently runs, for the create-tenant override
 * pre-fill (PTV2-1021 Phase 4). Gated on DM availability — pass
 * `useDmAvailable().available` so the query is `enabled` only when the override
 * section can actually be shown (mirrors how `useDockerHubCredentials` takes an
 * `enabled` flag). Base versions change rarely, so a 5-minute `staleTime`.
 *
 * Like the health probe, any error (404 not-in-discovery / 503 down / network /
 * a flaky probe) is treated as "no base versions" and resolves the all-null map
 * rather than surfacing an error — the dialog then simply shows empty inputs
 * (current behavior). `retry:false` (it's a best-effort pre-fill, not load-
 * bearing). A 401 still flows through the http layer's global redirect.
 */
export function useBaseVersions(enabled = true): UseQueryResult<BaseVersionsViewModel> {
  return useQuery<BaseVersionsViewModel>({
    queryKey: baseVersionsKey,
    enabled,
    retry: false,
    staleTime: BASE_VERSIONS_STALE_MS,
    queryFn: async ({ signal }) => {
      try {
        return mapBaseVersions(await apiClient.getBaseVersions({ signal }));
      } catch (error) {
        // 404/503/network → DM unavailable → no pre-fill (empty inputs). Any
        // other transport/HTTP failure is likewise non-fatal here: the pre-fill
        // is a convenience, never a blocker. A non-ApiError (programmer error)
        // still propagates.
        if (error instanceof ApiError) return NO_BASE_VERSIONS;
        throw error;
      }
    },
  });
}

/**
 * DockerHub credentials. 404 = "no credentials configured yet" — surfaced as
 * `null` data, NOT an error, so the dialog shows an empty form. The PUT
 * response (and GET) NEVER carry the token; only `tokenSet`.
 */
export function useDockerHubCredentials(
  enabled = true,
): UseQueryResult<DockerHubCredentialsViewModel | null> {
  return useQuery<DockerHubCredentialsViewModel | null>({
    queryKey: dockerHubCredentialsKey,
    enabled,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        return mapDockerHubCredentials(await apiClient.getDockerhubCredentials({ signal }));
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

/**
 * Single tenant detail. Polls every 2 s while the tenant's `deploymentStatus`
 * is non-terminal (so the detail view's status pill + override actions track a
 * running deploy/update/stop/wipe job at the same cadence as the list), then
 * stops. Retries via the app-wide `shouldRetry` policy, which bails on any 4xx
 * (incl. the 404 raised when the tenant is wiped out from under us) — so we
 * stop polling a gone tenant; the detail view treats that 404 as "wipe
 * completed" and navigates away.
 */
export function useTenant(uuid: string | null): UseQueryResult<TenantViewModel> {
  return useQuery<TenantViewModel>({
    queryKey: tenantQueryKey(uuid ?? ''),
    enabled: uuid !== null && uuid.length > 0,
    retry: shouldRetry,
    queryFn: async ({ signal }) => mapTenant(await apiClient.getTenant(uuid ?? '', { signal })),
    refetchInterval: (query) =>
      isNonTerminalDeploymentStatus(query.state.data?.deploymentStatus) ? POLL_INTERVAL_MS : false,
  });
}

export interface TenantUsersQueryParams {
  readonly tenantUuid: string;
  readonly page: number;
  readonly size: number;
  /** Trimmed search query. Empty falls back to the unfiltered `GET /users` list. */
  readonly q?: string;
}

/**
 * Users inside one tenant (tenant-admin reach, PTV2-1426). Switches between `GET /users` and
 * `GET /users/search` based on whether `q` is non-empty, mirroring `useAdminUsers`. A 404 here means
 * the caller lacks (or the tenant lacks) tenant-admin reach for this tenant — anti-enumeration, so it
 * is surfaced as a normal query error for the caller to render as "access denied", not retried.
 */
export function useTenantUsers(params: TenantUsersQueryParams): UseQueryResult<TenantUsersPageViewModel> {
  const trimmed = (params.q ?? '').trim();
  return useQuery<TenantUsersPageViewModel>({
    queryKey: tenantUsersListQueryKey(params.tenantUuid, params.page, params.size, trimmed),
    enabled: params.tenantUuid.length > 0,
    retry: shouldRetry,
    queryFn: async ({ signal }) => {
      const page = trimmed.length > 0
        ? await apiClient.searchTenantUsers(
            params.tenantUuid,
            { q: trimmed, page: params.page, size: params.size },
            { signal },
          )
        : await apiClient.listTenantAdminUsers(
            params.tenantUuid,
            { page: params.page, size: params.size },
            { signal },
          );
      const rows = page.content.map(mapAdminUser);
      return {
        rows,
        page: page.number ?? params.page,
        size: page.size ?? params.size,
        totalElements: page.totalElements ?? rows.length,
        totalPages:
          page.totalPages ??
          Math.max(1, Math.ceil((page.totalElements ?? rows.length) / Math.max(params.size, 1))),
      };
    },
  });
}

/**
 * A tenant's own role catalogue (tenant-admin reach). Same 404-is-access-denied semantics as
 * {@link useTenantUsers}. Used both by the tenant detail "Roles" tab and the auth-provider role
 * pickers (PTV2-1426).
 */
export function useTenantRoles(
  tenantUuid: string,
  q?: string,
): UseQueryResult<TenantRoleViewModel[]> {
  const trimmed = (q ?? '').trim();
  return useQuery<TenantRoleViewModel[]>({
    queryKey: tenantRolesListQueryKey(tenantUuid, trimmed),
    enabled: tenantUuid.length > 0,
    retry: shouldRetry,
    queryFn: async ({ signal }) => {
      const list = await apiClient.listTenantRoles(
        tenantUuid,
        trimmed.length > 0 ? trimmed : undefined,
        { signal },
      );
      return list.map(mapTenantRole);
    },
  });
}

const NO_JOB_ID = -1;

/**
 * Job execution detail. Enabled only when an id is present; polls every 2 s
 * while the job is in a non-terminal Spring Batch state, then stops. (A job
 * can be `STARTING`/`COMPLETING`/`UNKNOWN`, not just `STARTED`, so we key off
 * `isTerminal` rather than a single status string.) `UNKNOWN` is non-terminal
 * by Spring Batch semantics, so a job genuinely stuck in `UNKNOWN` will poll
 * for as long as the component is mounted — that is bounded by the detail view
 * unmounting, which is acceptable here (CR8: documented, not bounded further).
 *
 * The key and fetch share one `NO_JOB_ID` sentinel; the `enabled` guard means
 * the queryFn never actually runs with the sentinel.
 */
export function useJob(jobId: number | null): UseQueryResult<JobExecutionViewModel> {
  return useQuery<JobExecutionViewModel>({
    queryKey: jobQueryKey(jobId ?? NO_JOB_ID),
    enabled: jobId !== null,
    queryFn: async ({ signal }) => mapJob(await apiClient.getJob(jobId ?? NO_JOB_ID, { signal })),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === undefined) return false;
      return isTerminalStatus(status) ? false : POLL_INTERVAL_MS;
    },
  });
}

/**
 * Job list. The endpoint is context-routed by the backend: a global-plane user
 * sees cluster/global jobs; a tenant user sees only their tenant's jobs. The
 * frontend does NOT branch on plane — it renders whatever comes back.
 *
 * Polls every 2 s while any job in the list is in a non-terminal Batch state
 * (`STARTING | STARTED | STOPPING | COMPLETING | UNKNOWN`); stops once all
 * jobs are terminal. Mirrors the job-detail polling in `useJob`.
 */
export function useJobs(params?: ListJobsParams): UseQueryResult<JobExecutionViewModel[]> {
  return useQuery<JobExecutionViewModel[]>({
    queryKey: [...jobsListKey, params ?? {}] as const,
    queryFn: async ({ signal }) => {
      const list = await apiClient.listJobs(params, { signal });
      return list.map(mapJob);
    },
    refetchInterval: (query) => {
      const jobs = query.state.data;
      if (jobs === undefined || jobs.length === 0) return false;
      return jobs.some((j) => !isTerminalStatus(j.status)) ? POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Restart a FAILED or STOPPED job. Returns the new `JobExecutionViewModel` for
 * the restarted execution. Invalidates the jobs list on settle so the list
 * refreshes automatically.
 */
export function useRestartJob(): UseMutationResult<JobExecutionViewModel, Error, number> {
  const invalidate = useInvalidatePlatform();
  return useMutation<JobExecutionViewModel, Error, number>({
    mutationFn: async (jobId) => mapJob(await apiClient.restartJob(jobId)),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useTenantTemplates(): UseQueryResult<TenantTemplateViewModel[]> {
  return useQuery<TenantTemplateViewModel[]>({
    queryKey: tenantTemplatesKey,
    queryFn: async ({ signal }) => {
      const list = await apiClient.listTenantTemplates({ signal });
      return list.map(mapTenantTemplate);
    },
  });
}

export function useClusterGrants(
  filters: ClusterGrantFilters,
): UseQueryResult<ClusterGrantViewModel[]> {
  return useQuery<ClusterGrantViewModel[]>({
    queryKey: clusterGrantsListQueryKey(filters),
    queryFn: async ({ signal }) => {
      const list = await apiClient.listClusterGrants(
        {
          ...(filters.globalUserGuid !== undefined && filters.globalUserGuid !== ''
            ? { globalUserGuid: filters.globalUserGuid }
            : {}),
          ...(filters.tenantUuid !== undefined && filters.tenantUuid !== ''
            ? { tenantUuid: filters.tenantUuid }
            : {}),
        },
        { signal },
      );
      return list.map(mapClusterGrant);
    },
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface ServiceOverrideInput {
  readonly serviceName: OverridableServiceName;
  readonly imageTag: string;
}

export interface CreateTenantInput {
  readonly name: string;
  readonly label?: string;
  readonly state?: TenantState;
  /** When non-empty (and DM reachable), create returns 202 + a deploy job. */
  readonly serviceOverrides?: readonly ServiceOverrideInput[];
}

/**
 * Result of creating a tenant. A plain create resolves the created tenant
 * (201). A create-with-overrides resolves 202 + a job; the backend may return
 * either the override-launch shape (`jobExecutionId`) or a partial tenant —
 * `launchedJobExecutionId` is populated when a deploy job was kicked off so the
 * page can immediately subscribe via `useJob`.
 *
 * `overridesRequestedButNotDeployed` is true when the operator asked for
 * overrides but the create resolved 201 with NO launched job — i.e. the
 * deployment manager was unreachable server-side at create time. The override
 * rows are saved on the tenant but nothing was deployed; the UI must say so
 * explicitly (CR5) rather than implying a silent success.
 */
export interface CreateTenantResult {
  readonly tenant: TenantViewModel | null;
  readonly launchedJobExecutionId: number | null;
  readonly overridesRequestedButNotDeployed: boolean;
}

function toServiceOverrideRequest(o: ServiceOverrideInput): ServiceOverrideRequestDto {
  return { serviceName: o.serviceName, imageTag: o.imageTag.trim() };
}

/**
 * The create-with-overrides 202 shape is not fully nailed down across services
 * yet (RE-VERIFY at integration): it may be an `OverrideJobLaunchDto`
 * (`jobExecutionId`), or a `TenantDto` whose `current_job_execution_id` carries
 * the deploy job. This extracts the job id from whichever arrives, tolerating
 * both, and never throws on an unexpected shape.
 *
 * `overridesRequested` lets us flag the 201-with-overrides-but-no-job case (DM
 * down server-side): a TenantDto came back with no launched job even though the
 * operator asked for overrides.
 */
function extractCreateLaunch(raw: unknown, overridesRequested: boolean): CreateTenantResult {
  if (raw != null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    const launchId = readLaunchJobId(obj);
    if (launchId !== null) {
      return { tenant: null, launchedJobExecutionId: launchId, overridesRequestedButNotDeployed: false };
    }
    if (typeof obj.uuid === 'string') {
      const tenant = mapTenant(raw as TenantDto);
      const launched = tenant.currentJobExecutionId;
      return {
        tenant,
        launchedJobExecutionId: launched,
        overridesRequestedButNotDeployed: overridesRequested && launched === null,
      };
    }
  }
  return {
    tenant: null,
    launchedJobExecutionId: null,
    overridesRequestedButNotDeployed: overridesRequested,
  };
}

export interface UpdateTenantInput {
  readonly uuid: string;
  readonly label?: string;
  readonly state?: TenantState;
}

function useInvalidatePlatform(): () => Promise<void> {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: platformRootKey }).then(() => undefined);
}

export function useCreateTenant(): UseMutationResult<CreateTenantResult, Error, CreateTenantInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<CreateTenantResult, Error, CreateTenantInput>({
    mutationFn: async (input) => {
      const overrides = (input.serviceOverrides ?? []).map(toServiceOverrideRequest);
      const body: CreateTenantRequestDto = {
        name: input.name,
        ...(input.label !== undefined ? { label: input.label } : {}),
        ...(input.state !== undefined ? { state: input.state } : {}),
        ...(overrides.length > 0 ? { serviceOverrides: overrides } : {}),
      };
      // `createTenant` is typed to TenantDto for the 201 path; the 202
      // overrides path returns a different shape, so read it as `unknown` and
      // let `extractCreateLaunch` tolerate both.
      const raw: unknown = await apiClient.createTenant(body);
      return extractCreateLaunch(raw, overrides.length > 0);
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useUpdateTenant(): UseMutationResult<TenantViewModel, Error, UpdateTenantInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantViewModel, Error, UpdateTenantInput>({
    mutationFn: async ({ uuid, label, state }) => {
      const body: UpdateTenantRequestDto = {
        ...(label !== undefined ? { label } : {}),
        ...(state !== undefined ? { state } : {}),
      };
      return mapTenant(await apiClient.updateTenant(uuid, body));
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

/** Re-init resolves with the launched job id so the caller can subscribe via `useJob`. */
export function useReinitTenant(): UseMutationResult<JobLaunchDto, Error, string> {
  const invalidate = useInvalidatePlatform();
  return useMutation<JobLaunchDto, Error, string>({
    mutationFn: (uuid) => apiClient.initTenant(uuid),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useSeedTenant(): UseMutationResult<unknown, Error, string> {
  const invalidate = useInvalidatePlatform();
  return useMutation<unknown, Error, string>({
    mutationFn: (uuid) => apiClient.seedTenant(uuid),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface ProvisionUserInput {
  readonly tenantUuid: string;
  readonly email: string;
  readonly name: string;
  readonly fullName?: string;
  readonly roles: readonly string[];
}

export function useProvisionTenantUser(): UseMutationResult<unknown, Error, ProvisionUserInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<unknown, Error, ProvisionUserInput>({
    mutationFn: ({ tenantUuid, email, name, fullName, roles }) => {
      // Omit fullName entirely when blank (conditional spread, matching
      // useCreateTenant) rather than sending an empty string.
      const body: ProvisionTenantUserRequestDto = {
        email,
        name,
        roles,
        ...(fullName !== undefined && fullName.length > 0 ? { fullName } : {}),
      };
      return apiClient.provisionTenantUser(tenantUuid, body);
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface ReplaceUserRolesInput {
  readonly tenantUuid: string;
  readonly userGuid: string;
  readonly roles: readonly string[];
}

export function useReplaceTenantUserRoles(): UseMutationResult<
  undefined,
  Error,
  ReplaceUserRolesInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<undefined, Error, ReplaceUserRolesInput>({
    mutationFn: ({ tenantUuid, userGuid, roles }) => {
      const body: ReplaceTenantUserRolesRequestDto = { roles };
      return apiClient.replaceTenantUserRoles(tenantUuid, userGuid, body);
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface TenantUserActionInput {
  readonly tenantUuid: string;
  readonly userGuid: string;
}

export function useActivateTenantUser(): UseMutationResult<
  TenantUserViewModel,
  Error,
  TenantUserActionInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantUserViewModel, Error, TenantUserActionInput>({
    mutationFn: async ({ tenantUuid, userGuid }) =>
      mapAdminUser(await apiClient.activateTenantUser(tenantUuid, userGuid)),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useDeactivateTenantUser(): UseMutationResult<
  undefined,
  Error,
  TenantUserActionInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<undefined, Error, TenantUserActionInput>({
    mutationFn: ({ tenantUuid, userGuid }) => apiClient.deactivateTenantUser(tenantUuid, userGuid),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface CreateTenantRoleInput {
  readonly tenantUuid: string;
  readonly name: string;
  readonly description?: string;
}

export function useCreateTenantRole(): UseMutationResult<
  TenantRoleViewModel,
  Error,
  CreateTenantRoleInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantRoleViewModel, Error, CreateTenantRoleInput>({
    mutationFn: async ({ tenantUuid, name, description }) => {
      const body: TenantRoleDto = {
        name,
        ...(description !== undefined && description.length > 0 ? { description } : {}),
      };
      return mapTenantRole(await apiClient.createTenantRole(tenantUuid, body));
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

/** Only `description` is mutable server-side (`RoleServiceImpl.updateDescription` ignores `name`). */
export interface UpdateTenantRoleInput {
  readonly tenantUuid: string;
  readonly guid: string;
  readonly name: string;
  readonly description?: string;
}

export function useUpdateTenantRole(): UseMutationResult<
  TenantRoleViewModel,
  Error,
  UpdateTenantRoleInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantRoleViewModel, Error, UpdateTenantRoleInput>({
    mutationFn: async ({ tenantUuid, guid, name, description }) => {
      const body: TenantRoleDto = {
        name,
        ...(description !== undefined && description.length > 0 ? { description } : {}),
      };
      return mapTenantRole(await apiClient.updateTenantRole(tenantUuid, guid, body));
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface DeleteTenantRoleInput {
  readonly tenantUuid: string;
  readonly guid: string;
}

export function useDeleteTenantRole(): UseMutationResult<undefined, Error, DeleteTenantRoleInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<undefined, Error, DeleteTenantRoleInput>({
    mutationFn: ({ tenantUuid, guid }) => apiClient.deleteTenantRole(tenantUuid, guid),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

/** A single tenant role by guid (PTV2-1444 role page). */
export function useTenantRole(
  tenantUuid: string,
  guid: string,
): UseQueryResult<TenantRoleViewModel> {
  return useQuery<TenantRoleViewModel>({
    queryKey: tenantRoleQueryKey(tenantUuid, guid),
    queryFn: async ({ signal }) =>
      mapTenantRole(await apiClient.getTenantRole(tenantUuid, guid, { signal })),
    enabled: tenantUuid.length > 0 && guid.length > 0,
  });
}

// ---------------------------------------------------------------------------
// Role permissions + grammar vocabulary (PTV2-1441 / 1442)
// ---------------------------------------------------------------------------

/** The strongly-typed permission grammar vocabulary; cached indefinitely (it is static per build). */
export function usePermissionVocabulary(): UseQueryResult<PermissionVocabularyViewModel> {
  return useQuery<PermissionVocabularyViewModel>({
    queryKey: permissionVocabularyKey,
    queryFn: async ({ signal }) =>
      mapPermissionVocabulary(await apiClient.getPermissionVocabulary({ signal })),
    staleTime: Infinity,
  });
}

/** A single role's assigned grammar permissions; `enabled` gates the fetch to when the editor is open. */
export function useTenantRolePermissions(
  tenantUuid: string,
  guid: string,
  enabled = true,
): UseQueryResult<string[]> {
  return useQuery<string[]>({
    queryKey: tenantRolePermissionsQueryKey(tenantUuid, guid),
    queryFn: async ({ signal }) => {
      const dto = await apiClient.getTenantRolePermissions(tenantUuid, guid, { signal });
      return [...dto.permissions];
    },
    enabled: enabled && guid.length > 0,
  });
}

export interface ReplaceRolePermissionsInput {
  readonly tenantUuid: string;
  readonly guid: string;
  readonly permissions: readonly string[];
}

/** Replace-all a role's permissions (escalation-guarded server-side); busts the platform cache. */
export function useReplaceTenantRolePermissions(): UseMutationResult<
  string[],
  Error,
  ReplaceRolePermissionsInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<string[], Error, ReplaceRolePermissionsInput>({
    mutationFn: async ({ tenantUuid, guid, permissions }) => {
      const dto = await apiClient.replaceTenantRolePermissions(tenantUuid, guid, {
        permissions: [...permissions],
      });
      return [...dto.permissions];
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface ApplyTemplateInput {
  readonly tenantUuid: string;
  readonly templateName: string;
}

export function useApplyTenantTemplate(): UseMutationResult<JobLaunchDto, Error, ApplyTemplateInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<JobLaunchDto, Error, ApplyTemplateInput>({
    mutationFn: ({ tenantUuid, templateName }) => {
      const body: ApplyTenantTemplateRequestDto = { templateName };
      return apiClient.applyTenantTemplate(tenantUuid, body);
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export interface CreateTemplateInput {
  readonly name: string;
  readonly description?: string;
  readonly definition?: unknown;
}

export function useCreateTenantTemplate(): UseMutationResult<
  TenantTemplateViewModel,
  Error,
  CreateTemplateInput
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantTemplateViewModel, Error, CreateTemplateInput>({
    mutationFn: async ({ name, description, definition }) => {
      const body: CreateTenantTemplateRequestDto = {
        name,
        ...(description !== undefined ? { description } : {}),
        ...(definition !== undefined ? { definition } : {}),
      };
      return mapTenantTemplate(await apiClient.createTenantTemplate(body));
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useCreateClusterGrant(): UseMutationResult<
  ClusterGrantViewModel,
  Error,
  CreateClusterGrantRequestDto
> {
  const invalidate = useInvalidatePlatform();
  return useMutation<ClusterGrantViewModel, Error, CreateClusterGrantRequestDto>({
    mutationFn: async (body) => mapClusterGrant(await apiClient.createClusterGrant(body)),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

export function useRevokeClusterGrant(): UseMutationResult<undefined, Error, number> {
  const invalidate = useInvalidatePlatform();
  return useMutation<undefined, Error, number>({
    mutationFn: (id) => apiClient.revokeClusterGrant(id),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

// ── Phase 2: override sub-actions (deploy/update/stop/wipe) + edit + creds ──

/**
 * Edit overrides via `PUT /tenants/{uuid}` (per the API contract's "Edit
 * overrides" row). Synchronous update — surfaces a "Re-deploy to apply" hint in
 * the UI afterward. An empty `serviceOverrides` array clears all overrides.
 */
export interface EditOverridesInput {
  readonly uuid: string;
  readonly serviceOverrides: readonly ServiceOverrideInput[];
}

export function useEditOverrides(): UseMutationResult<TenantViewModel, Error, EditOverridesInput> {
  const invalidate = useInvalidatePlatform();
  return useMutation<TenantViewModel, Error, EditOverridesInput>({
    mutationFn: async ({ uuid, serviceOverrides }) => {
      const body: UpdateTenantRequestDto = {
        serviceOverrides: serviceOverrides.map(toServiceOverrideRequest),
      };
      return mapTenant(await apiClient.updateTenant(uuid, body));
    },
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

/**
 * Defensively read the launched Spring Batch execution id from an override
 * sub-action / wipe 202 (`DeploymentActionResponse`) or a core job launch.
 * The backend DTO is `@JsonInclude(NON_NULL)`, so `jobExecutionId` can be
 * ABSENT from the wire body; trusting the non-optional type would yield
 * `undefined` and a never-terminal `useJob`. Returns `null` when no usable
 * numeric id is present so the caller can fail loudly instead of polling a
 * phantom job. The core job DTO carries the id as a STRING `jobId`, so both
 * shapes are tolerated.
 */
export function readLaunchJobId(launch: { jobExecutionId?: unknown }): number | null {
  const raw = launch.jobExecutionId;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
    return Number(raw);
  }
  return null;
}

/**
 * Defensively read the launched id from a core `PhenotipsJobExecutionDTO`
 * (`jobId` is a STRING on the wire). Returns `null` when not a finite number.
 */
export function readCoreJobId(launch: { jobId?: unknown }): number | null {
  const raw = launch.jobId;
  if (typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))) {
    return Number(raw);
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  return null;
}

function useOverrideAction(
  call: (uuid: string) => Promise<OverrideJobLaunchDto>,
): UseMutationResult<OverrideJobLaunchDto, Error, string> {
  const invalidate = useInvalidatePlatform();
  return useMutation<OverrideJobLaunchDto, Error, string>({
    mutationFn: (uuid) => call(uuid),
    onSettled: () => {
      invalidate().catch(() => undefined);
    },
  });
}

/** Deploy/update/stop each launch a job (202) and resolve with its execution id. */
export function useDeployTenant(): UseMutationResult<OverrideJobLaunchDto, Error, string> {
  return useOverrideAction((uuid) => apiClient.deployTenant(uuid));
}

export function useUpdateTenantDeployment(): UseMutationResult<OverrideJobLaunchDto, Error, string> {
  return useOverrideAction((uuid) => apiClient.updateTenantDeployment(uuid));
}

export function useStopTenant(): UseMutationResult<OverrideJobLaunchDto, Error, string> {
  return useOverrideAction((uuid) => apiClient.stopTenant(uuid));
}

/**
 * DESTRUCTIVE: drops the tenant schema. `DELETE /tenants/{uuid}` → 202; tenant
 * shows `wiping` until the job completes. Gated to managePlatform + DM-reachable
 * at the call site; this hook does not re-check that.
 */
export function useWipeTenant(): UseMutationResult<OverrideJobLaunchDto, Error, string> {
  return useOverrideAction((uuid) => apiClient.wipeTenant(uuid));
}

export interface SetDockerHubCredentialsInput {
  readonly username: string;
  readonly token: string;
}

/**
 * PUT DockerHub credentials. The token is write-only and never echoed back —
 * the response carries only `tokenSet`. We do NOT keep the token anywhere; the
 * mutation input is discarded after the call.
 */
export function useSetDockerHubCredentials(): UseMutationResult<
  DockerHubCredentialsViewModel,
  Error,
  SetDockerHubCredentialsInput
> {
  const queryClient = useQueryClient();
  return useMutation<DockerHubCredentialsViewModel, Error, SetDockerHubCredentialsInput>({
    mutationFn: async ({ username, token }) => {
      const body: PutDockerHubCredentialsRequestDto = { username, token };
      return mapDockerHubCredentials(await apiClient.putDockerhubCredentials(body));
    },
    onSettled: () => {
      queryClient
        .invalidateQueries({ queryKey: dockerHubCredentialsKey })
        .catch(() => undefined);
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant-reach federated identities (PTV2-1472)
// ---------------------------------------------------------------------------

/** Root key for one tenant's federated-identity lists — a single node to invalidate on any link/unlink. */
export function tenantFederatedIdentitiesRootKey(
  tenantUuid: string,
): readonly [...typeof tenantsRootKey, string, 'federated-identities'] {
  return [...tenantsRootKey, tenantUuid, 'federated-identities'] as const;
}

const TENANT_USER_SEARCH_MIN_LENGTH = 1;
const TENANT_USER_SEARCH_DEBOUNCE_MS = 200;
const TENANT_USER_SEARCH_LIMIT = 25;

/**
 * A tenant's captured external identities, filtered by lifecycle `status` (the admin inbox default) OR
 * by linked `userGuid`. Runs through the tenant-admin reach path, so a caller lacking management access
 * to this tenant gets a 404 (see {@link isTenantAdminAccessDenied}). `enabled` gates on a resolved uuid.
 */
export function useTenantFederatedIdentities(
  tenantUuid: string,
  params: FederatedIdentitiesQuery,
  options?: { enabled?: boolean },
): UseQueryResult<FederatedIdentityViewModel[]> {
  return useQuery<FederatedIdentityViewModel[]>({
    queryKey: [
      ...tenantFederatedIdentitiesRootKey(tenantUuid),
      'list',
      params.status ?? null,
      params.userGuid ?? null,
    ] as const,
    enabled: (options?.enabled ?? true) && tenantUuid.length > 0,
    queryFn: async ({ signal }) => {
      const list = await apiClient.listTenantFederatedIdentities(tenantUuid, params, { signal });
      return list.map(mapFederatedIdentity);
    },
  });
}

/** Link/unlink a tenant's captured identity. Any mutation refreshes every status list for the tenant. */
export function useTenantFederatedIdentityMutations(tenantUuid: string): {
  readonly link: UseMutationResult<FederatedIdentityViewModel, Error, { guid: string; userGuid: string }>;
  readonly unlink: UseMutationResult<undefined, Error, string>;
} {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    queryClient
      .invalidateQueries({ queryKey: tenantFederatedIdentitiesRootKey(tenantUuid) })
      .catch(() => undefined);
  };

  const link = useMutation<FederatedIdentityViewModel, Error, { guid: string; userGuid: string }>({
    mutationFn: async ({ guid, userGuid }) =>
      mapFederatedIdentity(
        await apiClient.linkTenantFederatedIdentity(tenantUuid, guid, { user_guid: userGuid }),
      ),
    onSettled: invalidate,
  });

  const unlink = useMutation<undefined, Error, string>({
    mutationFn: (guid) => apiClient.unlinkTenantFederatedIdentity(tenantUuid, guid),
    onSettled: invalidate,
  });

  return { link, unlink };
}

/**
 * Live user typeahead scoped to ONE tenant (PTV2-1472) — the tenant analogue of
 * {@code usePlatformUserSearch}, backing the identity link picker and the user filter. Hits
 * {@code GET /tenants/{uuid}/users/search}. Debounced, min-length gated, returns picker options.
 */
export function useTenantUserSearch(
  tenantUuid: string,
  rawQuery: string,
): { readonly results: readonly UserPickerOption[]; readonly isLoading: boolean } {
  const trimmed = rawQuery.trim();
  const debouncedQuery = useDebouncedValue(trimmed, TENANT_USER_SEARCH_DEBOUNCE_MS);
  const isActive = tenantUuid.length > 0 && debouncedQuery.length >= TENANT_USER_SEARCH_MIN_LENGTH;

  const query = useQuery<readonly UserPickerOption[]>({
    queryKey: [...tenantsRootKey, tenantUuid, 'users', 'search', debouncedQuery] as const,
    queryFn: async ({ signal }) => {
      const page = await apiClient.searchTenantUsers(
        tenantUuid,
        { q: debouncedQuery, page: 0, size: TENANT_USER_SEARCH_LIMIT },
        { signal },
      );
      return page.content.map(toTenantPickerOption);
    },
    enabled: isActive,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const debounceSettling = trimmed !== debouncedQuery;
  return {
    results: isActive ? (query.data ?? []) : [],
    isLoading: isActive && (debounceSettling || query.isFetching),
  };
}

function toTenantPickerOption(user: UserDto): UserPickerOption {
  const primary =
    user.fullName !== undefined && user.fullName.trim().length > 0
      ? user.fullName
      : (user.name ?? user.email ?? '');
  return {
    guid: user.uuid ?? '',
    fullName: primary,
    subtitle: user.email ?? null,
  };
}
